import React, { useState, useEffect, useMemo, useRef } from 'react';

export function CheckboxQuestion({ question, value, onChange, allAnswers = {} }) {
  // Initialize value structure if not present
  const currentValue = value || { selections: [], otherText: '' };
  const selections = currentValue.selections || [];
  const otherText = currentValue.otherText || '';

  const [showOtherInput, setShowOtherInput] = useState(selections.includes('outro'));
  const containerRef = useRef(null);

  // Resolve options - support both static arrays and dynamic functions
  const options = useMemo(() => {
    if (typeof question.options === 'function') {
      return question.options(allAnswers);
    }
    return question.options;
  }, [question.options, allAnswers]);

  const maxSelections = question.maxSelections || Infinity;
  const minSelections = question.minSelections || 0;
  const allowOther = question.allowOther || false;

  // exclusiveOptions: options that disable all others when selected
  // Can be a single value or an array of values
  const exclusiveOptions = question.exclusiveOptions || [];
  const exclusiveValues = Array.isArray(exclusiveOptions) ? exclusiveOptions : [exclusiveOptions];

  useEffect(() => {
    setShowOtherInput(selections.includes('outro'));
  }, [selections]);

  // Check if any exclusive option is selected
  const hasExclusiveSelected = selections.some(sel => exclusiveValues.includes(sel));

  const handleCheckboxChange = (optionValue) => {
    let newSelections = [...selections];

    if (newSelections.includes(optionValue)) {
      // Uncheck
      newSelections = newSelections.filter(v => v !== optionValue);

      // If unchecking "outro", clear the text
      if (optionValue === 'outro') {
        onChange({ selections: newSelections, otherText: '' });
        return;
      }
    } else {
      // Check - but only if we haven't reached max
      if (newSelections.length >= maxSelections) {
        return;
      }

      // If this option is exclusive, clear all other selections
      if (exclusiveValues.includes(optionValue)) {
        newSelections = [optionValue];
      }
      // If an exclusive option is already selected, replace it with this one
      else if (hasExclusiveSelected) {
        newSelections = [optionValue];
      }
      // Normal behavior: add to selections
      else {
        newSelections.push(optionValue);
      }
    }

    onChange({ selections: newSelections, otherText });

    // Scroll to navigation buttons if we've reached the limit
    if (newSelections.length >= maxSelections && optionValue !== 'outro') {
      setTimeout(() => {
        scrollToNavigationButtons();
      }, 100);
    }
  };

  const scrollToNavigationButtons = () => {
    // Find the navigation buttons container
    const questionCard = containerRef.current?.closest('.bg-white.rounded-2xl');
    if (questionCard) {
      const navButtons = questionCard.querySelector('button[class*="bg-gradient-to-r"]');
      if (navButtons) {
        // Get the footer height to offset scroll
        const footer = document.querySelector('footer');
        const footerHeight = footer ? footer.offsetHeight : 0;

        // Calculate the position to scroll to
        const navButtonsRect = navButtons.getBoundingClientRect();
        const scrollOffset = window.pageYOffset + navButtonsRect.top - (window.innerHeight - footerHeight - 100);

        // Scroll to position that shows the button above the footer with extra padding
        window.scrollTo({
          top: scrollOffset,
          behavior: 'smooth'
        });
      }
    }
  };

  const handleOtherTextChange = (text) => {
    onChange({ selections, otherText: text });
  };

  const isMaxReached = selections.length >= maxSelections;

  return (
    <div ref={containerRef} className="space-y-3">
      {options.map((opt) => {
        const isChecked = selections.includes(opt.value);
        const isExclusive = exclusiveValues.includes(opt.value);

        // Disable if:
        // 1. Not checked and max reached
        // 2. Not checked and ANY exclusive option is selected (disables everything else)
        const isDisabled =
          (!isChecked && isMaxReached) ||
          (!isChecked && hasExclusiveSelected);

        return (
          <label
            key={opt.value}
            className={`flex items-center p-3 sm:p-4 border-2 rounded-lg transition ${
              isDisabled
                ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                : 'border-gray-300 cursor-pointer hover:border-amber-400 hover:bg-amber-50'
            } ${isChecked ? 'border-amber-500 bg-amber-50' : ''}`}
          >
            <input
              type="checkbox"
              value={opt.value}
              checked={isChecked}
              onChange={() => handleCheckboxChange(opt.value)}
              disabled={isDisabled}
              className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500"
            />
            <span className="ml-2 sm:ml-3 text-sm sm:text-base text-gray-700 font-medium">{opt.label}</span>
          </label>
        );
      })}

      {allowOther && (
        <>
          <label
            className={`flex items-center p-3 sm:p-4 border-2 rounded-lg transition ${
              (!selections.includes('outro') && isMaxReached) || (!selections.includes('outro') && hasExclusiveSelected)
                ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                : 'border-gray-300 cursor-pointer hover:border-amber-400 hover:bg-amber-50'
            } ${selections.includes('outro') ? 'border-amber-500 bg-amber-50' : ''}`}
          >
            <input
              type="checkbox"
              value="outro"
              checked={selections.includes('outro')}
              onChange={() => handleCheckboxChange('outro')}
              disabled={(!selections.includes('outro') && isMaxReached) || (!selections.includes('outro') && hasExclusiveSelected)}
              className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500"
            />
            <span className="ml-2 sm:ml-3 text-sm sm:text-base text-gray-700 font-medium">Outro</span>
          </label>

          {showOtherInput && (
            <input
              type="text"
              value={otherText}
              onChange={(e) => handleOtherTextChange(e.target.value)}
              placeholder="Por favor, especifique..."
              className="w-full p-3 sm:p-4 text-sm sm:text-base border-2 border-gray-300 rounded-lg focus:border-amber-500 focus:outline-none transition"
            />
          )}
        </>
      )}

      {(minSelections > 0 || maxSelections < Infinity) && (
        <p className="text-sm text-gray-500 mt-2">
          {minSelections > 0 && maxSelections < Infinity
            ? `Selecione entre ${minSelections} e ${maxSelections} opções`
            : minSelections > 0
            ? `Selecione pelo menos ${minSelections} ${minSelections === 1 ? 'opção' : 'opções'}`
            : `Selecione até ${maxSelections} ${maxSelections === 1 ? 'opção' : 'opções'}`}
        </p>
      )}
    </div>
  );
}
